import { useCallback, useEffect, useRef, useState, type CSSProperties, type ComponentType } from 'react'
import { AnimatePresence } from 'framer-motion'
import { AppDialog } from '../Common/AppDialog'
import { Button } from '../Common/Button'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded'
import LocalFloristRoundedIcon from '@mui/icons-material/LocalFloristRounded'
import type { SpinTier } from '../../types/schema'
import { SPIN_TIERS } from '../../utils/gamificationRegistry'
import {
  adJourneyDoubled,
  completeAd,
  createAdJourney,
  dropAdJourney,
  isAdJourneySettled,
  startAdJourney,
  type AdJourneyState,
} from '../../utils/adRewardJourney'
import { activeAdProvider } from '../../services/adService'
import { isRaidCurseActiveError } from '../../utils/raidCurse'
import { useGamification } from '../../context/useGamification'
import { useAuth } from '../../context/useAuth'
import { useWorkspace } from '../../context/useWorkspace'
import { emitTrackEvent } from '../../utils/gamificationEvents'
import { fireAutomationEvent } from '../../services/automationBus'

const SPIN_TRACK_BY_TIER: Record<SpinTier, 'TRACK_LUCKY_SPINS' | 'TRACK_MEGA_SPINS' | 'TRACK_GIGA_SPINS'> = {
  LUCKY_SPIN: 'TRACK_LUCKY_SPINS',
  MEGA_SPIN: 'TRACK_MEGA_SPINS',
  GIGA_SPIN: 'TRACK_GIGA_SPINS',
}

type Stage = 'READY' | 'SPINNING' | 'RESULT' | 'AD_PLAYING' | 'SETTLED'

type SpinWheelModalProps = {
  isOpen: boolean
  tier: SpinTier | null
  onClose: () => void
}

const TIER_LABEL: Record<SpinTier, string> = {
  LUCKY_SPIN: 'Lucky Spin',
  MEGA_SPIN: 'Mega Spin',
  GIGA_SPIN: 'Giga Spin',
}

const TIER_ICON: Record<SpinTier, ComponentType<SvgIconProps>> = {
  LUCKY_SPIN: LocalFloristRoundedIcon,
  MEGA_SPIN: DiamondRoundedIcon,
  GIGA_SPIN: AutoAwesomeRoundedIcon,
}

const randomInTier = (tier: SpinTier) => {
  const { minCredits, maxCredits } = SPIN_TIERS[tier]
  return minCredits + Math.floor(Math.random() * (maxCredits - minCredits + 1))
}

/** One spin "session" — mounted fresh per open (keyed by tier) so its state
 *  always starts clean without a reset effect. */
const SpinWheelSession = ({ tier, onClose }: { tier: SpinTier; onClose: () => void }) => {
  const { spin, claimSpin } = useGamification()
  const { currentUser } = useAuth()
  const { activeWorkspaceId } = useWorkspace()
  const [stage, setStage] = useState<Stage>('READY')
  const [displayValue, setDisplayValue] = useState(0)
  const [baseValue, setBaseValue] = useState(0)
  const [claimId, setClaimId] = useState('')
  const [adProgress, setAdProgress] = useState(0)
  const [settledMessage, setSettledMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const flickerRef = useRef<number | null>(null)
  const claimedRef = useRef(false)

  const clearFlicker = () => {
    if (flickerRef.current !== null) window.clearInterval(flickerRef.current)
    flickerRef.current = null
  }

  useEffect(() => () => clearFlicker(), [])

  const commit = useCallback(
    async (doubled: boolean, message: string) => {
      if (claimedRef.current) return
      claimedRef.current = true
      try {
        await claimSpin(claimId, doubled)
        setSettledMessage(message)
      } catch (settleError) {
        setError(settleError instanceof Error ? settleError.message : 'Could not bank your credits.')
        setSettledMessage(message)
      } finally {
        setStage('SETTLED')
      }
    },
    [claimId, claimSpin],
  )

  const handleSpin = async () => {
    if (stage !== 'READY') return
    setError(null)
    setStage('SPINNING')
    flickerRef.current = window.setInterval(() => setDisplayValue(randomInTier(tier)), 70)

    try {
      const result = await spin(tier)
      // Count the spin against the per-tier mastery ladder. Fires before the
      // reveal animation lands so the unclaimedTrackTickets bucket updates in
      // step with the credit drop.
      emitTrackEvent(SPIN_TRACK_BY_TIER[tier])
      if (activeWorkspaceId && currentUser?.uid) {
        fireAutomationEvent(
          {
            type: 'SPIN_WON',
            workspaceId: activeWorkspaceId,
            eventId: `spin-${result.claimId}`,
            actorId: currentUser.uid,
            payload: { tier, credits: result.baseValue },
          },
          { id: currentUser.uid },
        )
      }
      window.setTimeout(() => {
        clearFlicker()
        setClaimId(result.claimId)
        setBaseValue(result.baseValue)
        setDisplayValue(result.baseValue)
        setStage('RESULT')
      }, 1100)
    } catch (spinError) {
      clearFlicker()
      // RaidCurseActiveError already carries the user-facing message.
      if (isRaidCurseActiveError(spinError)) {
        setError(spinError.message)
      } else {
        setError(spinError instanceof Error ? spinError.message : 'Spin failed.')
      }
      setStage('READY')
    }
  }

  const handleClaimBase = () => void commit(false, `+${baseValue} credits banked.`)

  // The masked ad journey: stream sequential ads; any blur/close drops the bonus.
  const runAdGauntlet = useCallback(
    async (initial: AdJourneyState) => {
      const controller = new AbortController()
      const abort = () => controller.abort()
      const onVisibility = () => {
        if (document.visibilityState === 'hidden') abort()
      }
      window.addEventListener('blur', abort)
      document.addEventListener('visibilitychange', onVisibility)
      window.addEventListener('beforeunload', abort)

      let journey = initial
      try {
        while (!isAdJourneySettled(journey)) {
          try {
            await activeAdProvider.playAd({
              index: journey.adsWatched,
              total: journey.totalAds,
              signal: controller.signal,
              onProgress: setAdProgress,
            })
            journey = completeAd(journey)
            setAdProgress(0)
          } catch {
            journey = dropAdJourney(journey)
          }
        }
      } finally {
        window.removeEventListener('blur', abort)
        document.removeEventListener('visibilitychange', onVisibility)
        window.removeEventListener('beforeunload', abort)
      }

      if (adJourneyDoubled(journey)) {
        await commit(true, `Bonus unlocked! +${baseValue * 2} credits banked.`)
      } else {
        await commit(false, `Bonus interrupted — your ${baseValue} base credits are safe.`)
      }
    },
    [baseValue, commit],
  )

  const handleDoubleRewards = () => {
    if (stage !== 'RESULT') return
    setStage('AD_PLAYING')
    setAdProgress(0)
    void runAdGauntlet(startAdJourney(createAdJourney(baseValue)))
  }

  // Closing mid-result must not burn the consumed ticket: auto-bank the base.
  const requestClose = () => {
    if (stage === 'AD_PLAYING') return // locked during the gauntlet
    if (stage === 'RESULT' && !claimedRef.current && baseValue > 0) {
      void commit(false, `+${baseValue} credits banked.`).then(onClose)
      return
    }
    clearFlicker()
    onClose()
  }

  return (
    <AppDialog
      open
      title={TIER_LABEL[tier]}
      onDismiss={requestClose}
      actions={[]}
    >
      <div className={`spinShell spinShell--${tier.toLowerCase()}`}>
        {stage === 'AD_PLAYING' ? (
          <div className="spinAdStage" aria-live="polite">
            <div
              className="spinAdRing"
              style={{ '--ad-progress': adProgress } as CSSProperties}
              aria-hidden="true"
            />
            <h2>Unlocking your bonus…</h2>
            <p className="spinMuted">Keep this window in focus to finish unlocking.</p>
          </div>
        ) : (
          <>
            <div className={`spinDial ${stage === 'SPINNING' ? 'isSpinning' : ''}`} aria-hidden="true">
              {(() => { const TierIcon = TIER_ICON[tier]; return <span><TierIcon fontSize="inherit" /></span> })()}
            </div>
            <div className="spinReadout" aria-live="polite">
              {stage === 'READY' ? '—' : displayValue}
              <span>credits</span>
            </div>

            {stage === 'READY' && (
              <Button variant="tertiary" size="md" onClick={() => void handleSpin()}>
                Spin
              </Button>
            )}

            {stage === 'SPINNING' && <p className="spinMuted">Rolling…</p>}

            {stage === 'RESULT' && (
              <>
                <h2 className="spinWinTitle">Yay! You won {baseValue} credits.</h2>
                <div className="spinActionRow">
                  <Button variant="tertiary" size="md" onClick={handleClaimBase}>
                    Claim Base Credits
                  </Button>
                  <Button variant="primary" size="md" icon={<AutoAwesomeRoundedIcon fontSize="inherit" />} onClick={handleDoubleRewards}>
                    Double Rewards
                  </Button>
                </div>
              </>
            )}

            {stage === 'SETTLED' && (
              <>
                <h2 className="spinWinTitle">{settledMessage}</h2>
                {error && <p className="spinError">{error}</p>}
                <Button variant="tertiary" size="md" onClick={onClose}>
                  Done
                </Button>
              </>
            )}

            {error && stage === 'READY' && <p className="spinError">{error}</p>}
          </>
        )}
      </div>
    </AppDialog>
  )
}

export const SpinWheelModal = ({ isOpen, tier, onClose }: SpinWheelModalProps) => (
  <AnimatePresence>
    {isOpen && tier && <SpinWheelSession key={tier} tier={tier} onClose={onClose} />}
  </AnimatePresence>
)
